/**
 * Load hand-maintained static overrides.
 *
 * Bus shapes preferred source (live):
 *   VITE_OVERRIDES_BUS_SHAPES_URL → GitHub raw (morgan-travelers-overrides repo)
 *   — assembled blob stub, then bus-shapes/index.json + per-route files
 * Fallback:
 *   public/overrides/bus-shapes/index.json + per-route files → offline bundle
 *
 * LRT / MTR access pins still load from public/overrides/.
 * Never generated by collect-open-data / build:fares.
 */

/** Published shapes repo on GitHub. */
export const OVERRIDES_REPO_DEFAULT = "UNGemini/morgan-travelers-overrides";

/**
 * Where to fetch bus-shapes.json (latest merged contributions):
 * 1. VITE_OVERRIDES_BUS_SHAPES_URL if set
 * 2. Same-origin /api/overrides/bus-shapes.json
 *    - Dev (Vite): sibling repo / local file
 *    - Prod (Pages Function): proxies GitHub raw (COEP-safe, fresh)
 * 3. Direct GitHub raw as last-resort URL (used if API fails)
 */
export function busShapesRemoteUrl() {
  if (
    typeof import.meta !== "undefined" &&
    import.meta.env?.VITE_OVERRIDES_BUS_SHAPES_URL
  ) {
    return String(import.meta.env.VITE_OVERRIDES_BUS_SHAPES_URL);
  }
  // Prefer same-origin API in browser (works under COEP; CF proxies GitHub)
  if (typeof window !== "undefined" && window.location?.origin) {
    return `${window.location.origin}/api/overrides/bus-shapes.json`;
  }
  return "/api/overrides/bus-shapes.json";
}

/** Direct GitHub raw URL (fallback when same-origin API is down). */
export function busShapesGithubRawUrl() {
  return `https://raw.githubusercontent.com/${OVERRIDES_REPO_DEFAULT}/main/bus-shapes.json`;
}

/**
 * Split index on GitHub raw — one JSON file per published route.
 * The overrides repo publishes this layout; the blob URL above is a stub
 * pointing here (see split_index).
 */
export function busShapesGithubSplitUrl() {
  return `https://raw.githubusercontent.com/${OVERRIDES_REPO_DEFAULT}/main/bus-shapes/index.json`;
}

/** @deprecated use busShapesRemoteUrl() */
export const DEFAULT_BUS_SHAPES_REMOTE = busShapesGithubRawUrl();

/** @type {any} */
const LRT_FALLBACK = {
  updated_at: "2026-04-24",
  stops: {
    "Tin Wing": {
      name_zh: "天榮",
      lat: 22.46,
      lon: 114.00222,
      code: "TWI",
      stop_id: "500",
    },
  },
  platforms: {
    "Tin Wing": {
      name_match: ["tin wing", "天榮"],
      centroid: { lon: 114.00222, lat: 22.46 },
      by_ref: {
        "1": { lon: 114.00248, lat: 22.45978 },
        "3": { lon: 114.00228, lat: 22.45995 },
        "4": { lon: 114.00212, lat: 22.46008 },
      },
    },
  },
  shapes: [
    {
      id: "ginza_to_tin_wing",
      from_match: ["ginza", "銀座"],
      to_match: ["tin wing", "天榮"],
      coordinates: [
        [114.004959, 22.457552],
        [114.00455, 22.45795],
        [114.0041, 22.45835],
        [114.00355, 22.45885],
        [114.00305, 22.45925],
        [114.00272, 22.45952],
        [114.00248, 22.45978],
      ],
    },
  ],
  approach_rules: [
    {
      id: "tin_wing_neighbour_approach",
      end_match: ["tin wing", "天榮"],
      use_shape: "ginza_to_tin_wing",
      slice_end: 4,
    },
  ],
};

/** @type {any} */
const MTR_ACCESS_FALLBACK = {
  updated_at: "2026-07-27",
  locked: [
    {
      name_en: "Sha Tin",
      name_zh: "沙田",
      lat: 22.3827,
      lon: 114.1875,
      code: "SHT",
    },
    {
      name_en: "Hong Kong",
      name_zh: "香港",
      code: "HOK",
      lock_coords_only: true,
    },
  ],
};

/** @type {any} */
const BUS_SHAPES_FALLBACK = {
  updated_at: "2026-07-31",
  note: "Reviewed bus path overrides (empty until mods publish contributions).",
  routes: [],
};

let lrtOverrides = LRT_FALLBACK;
let mtrAccessOverrides = MTR_ACCESS_FALLBACK;
let busShapeOverrides = BUS_SHAPES_FALLBACK;
let loadPromise = null;

function baseUrl() {
  const b =
    (typeof import.meta !== "undefined" && import.meta.env?.BASE_URL) || "/";
  return b.endsWith("/") ? b : `${b}/`;
}

/**
 * Try one bus-shapes URL; return parsed object or null.
 * @param {string} url
 * @param {string} label
 */
async function tryFetchBusShapes(url, label) {
  if (!url) return null;
  try {
    const bust = url.includes("?")
      ? `${url}&_=${Date.now()}`
      : `${url}?_=${Date.now()}`;
    const res = await fetch(bust, {
      cache: "no-store",
      mode: "cors",
      headers: { Accept: "application/json" },
    });
    if (!res.ok) {
      console.warn(`[overrides] bus-shapes ${label} HTTP`, res.status, url);
      return null;
    }
    const remote = await res.json();
    if (!Array.isArray(remote?.routes)) {
      console.warn(`[overrides] bus-shapes ${label} missing routes[]`);
      return null;
    }
    if (!remote.routes.length && (remote.split_index || remote.files?.length)) {
      return null;
    }
    return { data: remote, label, url };
  } catch (e) {
    console.warn(`[overrides] bus-shapes ${label} failed`, e);
    return null;
  }
}

/**
 * Load split contributed routes: index.json + one file per route.
 * @param {string} indexUrl
 * @param {string} label
 */
async function tryFetchSplitBusShapes(indexUrl, label) {
  if (!indexUrl) return null;
  try {
    const bust = indexUrl.includes("?")
      ? `${indexUrl}&_=${Date.now()}`
      : `${indexUrl}?_=${Date.now()}`;
    const res = await fetch(bust, {
      cache: "no-store",
      mode: "cors",
      headers: { Accept: "application/json" },
    });
    if (!res.ok) {
      console.warn(`[overrides] bus-shapes split ${label} HTTP`, res.status);
      return null;
    }
    const index = await res.json();
    const files = Array.isArray(index?.files) ? index.files : [];
    if (!files.length) return null;
    const base = new URL("./", indexUrl);
    const routes = [];
    const results = await Promise.all(
      files.map(async (f) => {
        const name = String(f || "").replace(/^.*\//, "");
        if (!name.endsWith(".json")) return null;
        const u = new URL(name, base).href;
        try {
          const r = await fetch(`${u}?_=${Date.now()}`, {
            cache: "no-store",
            headers: { Accept: "application/json" },
          });
          if (!r.ok) return null;
          return await r.json();
        } catch {
          return null;
        }
      }),
    );
    for (const rec of results) {
      if (rec && typeof rec === "object" && Array.isArray(rec.coordinates)) {
        routes.push(rec);
      }
    }
    if (!routes.length) return null;
    return {
      data: {
        updated_at: index.updated_at,
        note: index.note,
        routes,
      },
      label,
      url: indexUrl,
    };
  } catch (e) {
    console.warn(`[overrides] bus-shapes split ${label} failed`, e);
    return null;
  }
}

/**
 * Fetch overrides: LRT/access from app bundle; bus shapes from live GitHub (via API).
 */
export async function loadStaticOverrides() {
  if (loadPromise) return loadPromise;
  loadPromise = (async () => {
    if (typeof window === "undefined" || !window.location) {
      return {
        lrt: lrtOverrides,
        mtrAccess: mtrAccessOverrides,
        busShapes: busShapeOverrides,
      };
    }
    const root = baseUrl();
    try {
      const localBusUrl = new URL(
        `${root}overrides/bus-shapes.json`,
        window.location.href,
      ).href;
      const localSplitUrl = new URL(
        `${root}overrides/bus-shapes/index.json`,
        window.location.href,
      ).href;
      const apiUrl = busShapesRemoteUrl();
      const githubUrl = busShapesGithubRawUrl();
      const githubSplitUrl = busShapesGithubSplitUrl();
      // Same-origin split index next to the assembled blob endpoint
      const apiSplitUrl = apiUrl.replace(/bus-shapes\.json$/, "bus-shapes/index.json");

      const [lrtRes, pinRes] = await Promise.all([
        fetch(new URL(`${root}overrides/lrt.json`, window.location.href).href, {
          cache: "no-cache",
        }),
        fetch(
          new URL(`${root}overrides/mtr-access-pins.json`, window.location.href)
            .href,
          { cache: "no-cache" },
        ),
      ]);
      if (lrtRes.ok) lrtOverrides = await lrtRes.json();
      if (pinRes.ok) mtrAccessOverrides = await pinRes.json();

      // Prefer live merged shapes: same-origin API (GitHub proxy) →
      // same-origin split index → raw GitHub blob → raw split index → bundle
      let busSource = "fallback";
      const hit =
        (await tryFetchBusShapes(apiUrl, "api-proxy")) ||
        (await tryFetchSplitBusShapes(apiSplitUrl, "api-proxy-split")) ||
        (await tryFetchBusShapes(githubUrl, "github-raw")) ||
        (await tryFetchSplitBusShapes(githubSplitUrl, "github-raw-split")) ||
        (await tryFetchSplitBusShapes(localSplitUrl, "local-split")) ||
        (await tryFetchBusShapes(localBusUrl, "local-bundle"));

      if (hit) {
        busShapeOverrides = hit.data;
        busSource = hit.label;
      } else {
        busShapeOverrides = BUS_SHAPES_FALLBACK;
      }

      console.info(
        "[overrides]",
        "lrt shapes",
        lrtOverrides?.shapes?.length ?? 0,
        "access pins",
        mtrAccessOverrides?.locked?.length ?? 0,
        "bus shapes",
        busShapeOverrides?.routes?.length ?? 0,
        `(${busSource})`,
        "published",
        (busShapeOverrides?.routes || []).filter(
          (r) => !r.status || r.status === "published" || r.status === "approved",
        ).length,
      );
    } catch (err) {
      console.warn("[overrides] fetch failed — using fallback", err);
      lrtOverrides = LRT_FALLBACK;
      mtrAccessOverrides = MTR_ACCESS_FALLBACK;
      busShapeOverrides = BUS_SHAPES_FALLBACK;
    }
    return {
      lrt: lrtOverrides,
      mtrAccess: mtrAccessOverrides,
      busShapes: busShapeOverrides,
    };
  })();
  return loadPromise;
}

/**
 * Force re-fetch of bus shapes (e.g. after a long session or contribute merge).
 * Clears the load promise so next get/load hits the network again.
 */
export function invalidateBusShapeOverridesCache() {
  loadPromise = null;
  busShapeOverrides = BUS_SHAPES_FALLBACK;
}

/**
 * Reload bus shapes from network immediately.
 * @returns {Promise<any>}
 */
export async function reloadBusShapeOverrides() {
  invalidateBusShapeOverridesCache();
  return loadStaticOverrides();
}

export function getLrtOverrides() {
  return lrtOverrides;
}

export function getMtrAccessOverrides() {
  return mtrAccessOverrides;
}

export function getBusShapeOverrides() {
  return busShapeOverrides;
}

/** @returns {Set<string>} */
export function getAccessPinLockSet() {
  const set = new Set();
  for (const p of mtrAccessOverrides?.locked || []) {
    if (p?.name_en) set.add(String(p.name_en).toLowerCase());
  }
  return set;
}

/**
 * @returns {Map<string, { lat: number, lon: number, name_zh?: string, code?: string }>}
 */
export function getAccessPinCoords() {
  const map = new Map();
  for (const p of mtrAccessOverrides?.locked || []) {
    if (!p?.name_en) continue;
    if (Number.isFinite(p.lat) && Number.isFinite(p.lon)) {
      map.set(String(p.name_en).toLowerCase(), {
        lat: p.lat,
        lon: p.lon,
        name_zh: p.name_zh,
        code: p.code,
      });
    }
  }
  return map;
}
